<!--script context="module">
    // NOTE: Moved to separate store file previewstores.js. Keeping comment below just a bit longer
    // in case we have to revert.
    // Module context is necessary to export prop of this component.
    // In order to make a module context prop reactive, we define it as a store.
    import { writable } from 'svelte/store';
    export const selectedPreview = writable();
    export const roi = writable();
    export const finalFdkSettings = writable();
</script-->
<script>
    import { onMount, tick, createEventDispatcher, onDestroy } from "svelte";
    import { get } from "svelte/store";
    import { showDebug } from "../stores";
    import {
        selectedPreview,
        roi,
        finalFdkSettings,
        canvasSize,
        lastPreviewImageIndex,
        zoom,
        offset,
        histogramThreshold
    } from "../previewstores.js";
    import ChartHistogram from "svelte-material-icons/ChartHistogram.svelte";
    import ChevronLeft from "svelte-material-icons/ChevronLeft.svelte";
    import ChevronRight from "svelte-material-icons/ChevronRight.svelte";
    import RotateLeft from "svelte-material-icons/RotateLeft.svelte";
    import RotateRight from "svelte-material-icons/RotateRight.svelte";
    import Histogram from "../components/Histogram.svelte";
    import {
        getNumberOfAvailablePreviewsForSlice,
        extractDatasetName,
        extractSessionName,
        isoDateToTime,
        isoDateToDate,
        phaseRetrievalComparator,
        getCameraDisplayNameFromCalibrationId
    } from "../utils.js";
    import VectorSquare from "svelte-material-icons/VectorSquare.svelte";
    import MagnifyPlus from "svelte-material-icons/MagnifyPlus.svelte";
    import MagnifyMinus from "svelte-material-icons/MagnifyMinus.svelte";
    import HandBackRight from "svelte-material-icons/HandBackRight.svelte";
    import CropSquare from "svelte-material-icons/CropSquare.svelte";
    import { processImage } from "../imageTools.js";
    import ContentSave from "svelte-material-icons/ContentSave.svelte";
    import * as piexif from "piexifjs";
    import { tooltip } from "svooltip";
    import 'svooltip/styles.css';

    /**
     * Model (data)
     */
    export let type;
    export let headerText;
    export let previewImageIndex = $lastPreviewImageIndex; // Can be in range [0..63]
    export let sliceIndex;
    export let availableSlicePreviews;
    export let drawStatus;
    /**
     * Event handling
     */
    const dispatch = createEventDispatcher();

    /**
     * View and control (images)
     */
    let canvasWidth = $canvasSize; // Exported so that BeamhardCuppingPreview can reach the source data
    let previewImgSrc = "";

    let transparentImg;
    let panImage = false;
    let panActiv = false;
    let container;

    /**
     * Reactive effects:
     * - obtain the new valid previewImgSrc from the selectedPreview and the current image index.
     *   This should load the image object automatically.
     */
    $: { 
        console.log("SELECTED PREVIEW IS NOW ", $selectedPreview);
        console.log("AVAILABLE SLICE PREVIEWS UPDATED ", availableSlicePreviews);
        updateCarouselCount(); 
    }
    $: previewImgSrc = getPreviewUrl($selectedPreview, previewImageIndex);
    $: updateFdkSettingWithRoi($roi);
    $: $zoom, onCanvasSizeChanged($canvasSize);

    onMount(() => {
        transparentImg = new Image(0, 0);
        transparentImg.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
        document.addEventListener("keydown", panKey);
    });

    onDestroy(() => {
        document.removeEventListener("keydown", panKey);
    });
    /**
     * Final settings model handling.
     */
    function updateFdkSettingWithRoi(newRoi) {
        console.log("OLD ROI", $roi);
        $roi = newRoi;
        console.log("NEW ROI", $roi);

        if (!$finalFdkSettings) return;

        // If roi is updated, also update the final settings min_slice and max_slice
        // as well as the pixel roi
        let changedSettings = {
            min_slice: $roi.top,
            max_slice: $roi.bot,
            angle_$offset: 0,
            slice_roi: {
                left: $roi.left,
                right: $roi.right,
                top: $roi.left,
                bot: $roi.right,
            },
        };

        let currentFdkSettings = get(finalFdkSettings);

        currentFdkSettings = {
            ...currentFdkSettings,
            ...changedSettings,
        };
        return currentFdkSettings;
    }

    function getPreviewUrl(preview, previewImageIndex) {
        if (!isNumeric(previewImageIndex) || previewImageIndex < 0 /*|| previewImageIndex > 63*/) {
            // NOTE: If wide centershift, then we have a variable range of image indices, may be >> 63
            // If previewImageIndex is undefined, fetch the stored value
            previewImageIndex = $lastPreviewImageIndex;
        } else {
            // If we have a new previewImageIndex value, store it (the last used previewImageIndex) here.
            // TODO: Perhaps store this in the source preview components instead.
            $lastPreviewImageIndex = previewImageIndex;
        }

        // Modify the path to select the selected index (previewImageIndex, range [0-63])
        console.log(
            `GETTING PREVIEW URL FOR ${preview?.preview_input?.preview_input?.preview_type}, IMAGE INDEX=${previewImageIndex}`
        );
        console.log(preview);

        // Find the correct slice
        const previewSlice = preview?.preview_output?.find((s) => s.slice === sliceIndex);

        let previewUrl = previewSlice?.large_image_pattern;
        console.log("OLD PREVIEW IMAGE URL=", previewUrl);
        if (previewUrl) {
            let previewImageIndexFormatted = previewImageIndex.toLocaleString("en-US", {
                minimumIntegerDigits: 2,
                useGrouping: false,
            });
            let substituted = previewUrl.replace(/(%02d\.jpg)/i, previewImageIndexFormatted + ".jpg");
            console.log("NEW PREVIEW IMAGE URL=", substituted);
            previewUrl = substituted;
        } else {
            return "";
        }

        return previewUrl;
    }

    function getNumberOfAvailablePreviewsForCurrentSlice(sliceIndex) {
        return getNumberOfAvailablePreviewsForSlice(availableSlicePreviews, sliceIndex, type);
    }

    let carousel_count = undefined;
    let carousel_total = undefined;

    function updateCarouselCount() {
        // TODO: If possible, stay on the same preview group (iteration) when we switch between
        // preview types, even if we don't have the same number of previews as before
        const nearestPreviewsCount = getNumberOfAvailablePreviewsForCurrentSlice(sliceIndex);
        carousel_total = nearestPreviewsCount;

        // Find the current preview index of available and then set the carousel_count
        if (!carousel_count) {
            const previewIndex = availableSlicePreviews?.findIndex(p => p.preview_output[0].start_time == $selectedPreview.preview_output[0].start_time);
            if (previewIndex > -1)
                carousel_count = nearestPreviewsCount - previewIndex;
        }

        console.log(`UPDATING CAROUSEL FOR ${type} PREVIEW FOR SLICE ${sliceIndex}. TOTAL=${nearestPreviewsCount}`);
    }

    // Show the previous available preview
    function showPreviousPreview(sliceIndex) {
        console.log(`PREVIOUS ${type} PREVIEW OF SLICE ${sliceIndex}, CAROUSEL= `, carousel_count);

        const nearestPreviewsCount = getNumberOfAvailablePreviewsForCurrentSlice(sliceIndex);
        carousel_total = nearestPreviewsCount;

        console.log(`GETTING PREVIOUS ${type} PREVIEW FOR SLICE ${sliceIndex}. TOTAL=${nearestPreviewsCount}`);
        carousel_count = carousel_count - 1;
        if (!carousel_count || carousel_count < 1) carousel_count = nearestPreviewsCount;
        dispatch("carousel", { previewIndex: nearestPreviewsCount - carousel_count });
    }

    // Show the next available preview
    function showNextPreview(sliceIndex) {
        console.log(`NEXT ${type} PREVIEW OF SLICE ${sliceIndex},  CAROUSEL= `, carousel_count);

        const nearestPreviewsCount = getNumberOfAvailablePreviewsForCurrentSlice(sliceIndex);
        carousel_total = nearestPreviewsCount;

        console.log(`GETTING NEXT ${type} PREVIEW FOR SLICE ${sliceIndex}. TOTAL=${nearestPreviewsCount}`);
        carousel_count = carousel_count + 1;
        if (!carousel_count || carousel_count > nearestPreviewsCount) carousel_count = 1;
        dispatch("carousel", { previewIndex: nearestPreviewsCount - carousel_count });
    }

    function getHighestPhaseRetrieval() {
        let tmpAvailableSlicePreviews = [...availableSlicePreviews];
        let tmpAvailablePhs = tmpAvailableSlicePreviews.map(p => p.preview_input?.generic_preview_input?.phase_retrieval_settings?.phase_retrieval);
        tmpAvailablePhs?.sort(phaseRetrievalComparator);
        const arrayLength = tmpAvailablePhs?.length;
        if (!arrayLength) return undefined;
        const delta = tmpAvailablePhs?.[arrayLength-1]?.delta;
        const mu = tmpAvailablePhs?.[arrayLength-1]?.mu;
        const deltaByMu = delta / mu;
        return deltaByMu;
    }

    function getLowestPhaseRetrieval() {
        let tmpAvailableSlicePreviews = [...availableSlicePreviews];
        let tmpAvailablePhs = tmpAvailableSlicePreviews.map(p => p.preview_input?.generic_preview_input?.phase_retrieval_settings?.phase_retrieval);
        tmpAvailablePhs?.sort(phaseRetrievalComparator);
        const arrayLength = tmpAvailablePhs?.length;
        if (!arrayLength) return undefined;
        const delta = tmpAvailablePhs?.[0]?.delta;
        const mu = tmpAvailablePhs?.[0]?.mu;
        const deltaByMu = delta / mu;
        return deltaByMu;
    }

    function isNumeric(num) {
        return (typeof num === "number" || (typeof num === "string" && num.trim() !== "")) && !isNaN(num);
    }

    ///////////////// BEGIN canvas drawing & event handling functions
    // Create a canvas object for the source image in order to be able to read pixels from it
    export var sourceCanvas = document.createElement("canvas"); // Exported so that BeamhardCuppingPreview can reach the source data
    sourceCanvas.width = canvasWidth * $zoom;
    sourceCanvas.height = canvasWidth * $zoom;
    var sctx = sourceCanvas.getContext("2d");

    export var previewOverlayCanvas = undefined;
    var octx;

    let img;

    export let previewManipulationCanvas = undefined; // Exported so that BeamhardCuppingPreview can reach the source data (experimental)
    function onLoadImage(ev) {
        img = ev.target;
        console.log("LOADING IMAGE...", img, "[", img.width, ", ", img.height, "]");
        console.log(previewImgSrc);

        sctx.drawImage(img, 0, 0, img.width * $zoom, img.height * $zoom);

        // Clear the manipulation canvas
        previewManipulationCanvas = document.getElementById("previewManipulationCanvas");
        let mctx = previewManipulationCanvas.getContext("2d");
        mctx.clearRect(0, 0, img.width * $zoom, img.height * $zoom);
        mctx.drawImage(img, 0, 0, img.width * $zoom, img.height * $zoom);
        mctx.imageSmoothingEnabled = false;

        // On load of a new image, we also need to update the overlay canvas
        previewOverlayCanvas = document.getElementById("previewOverlayCanvas");
        octx = previewOverlayCanvas.getContext("2d");

        // Update histogram if visible
        if (isHistogramVisible) {
            onShowHistogram();
        }
        console.log($offset);
        sourceCanvas = sourceCanvas; // Svelte trick to trigger update of reactive prop
        previewManipulationCanvas = previewManipulationCanvas;

        previewOverlayCanvas = previewOverlayCanvas;
        preventOverlayContextMenu();

        previewManipulationCanvas.style.translate = `${$offset.x}px ${$offset.y}px`;
        const canvasContext = sourceCanvas.getContext("2d");
        processImage(
            canvasContext.getImageData(0, 0, canvasWidth * $zoom, canvasWidth * $zoom),
            previewManipulationCanvas,
            $histogramThreshold.min,
            $histogramThreshold.max
        );
    }

    // If canvasSize is changed, update local canvasWidth variable and both the manipulation canvas
    // and the overlay canvas
    async function onCanvasSizeChanged(updatedCanvasSize) {
        if (!updatedCanvasSize) return;
        if (!img) return;
        // Update the reactive canvasWidth variable. This will resize the manipulation canvas in the DOM
        canvasWidth = updatedCanvasSize;

        // Update source canvas size
        sourceCanvas.width = canvasWidth * $zoom;
        sourceCanvas.height = canvasWidth * $zoom;

        // Wait for previewManipulationCanvas DOM element to update
        await tick();

        // Once the manipulation canvas has been resized, redraw with pixel data from the source image
        previewManipulationCanvas = document.getElementById("previewManipulationCanvas");
        if (!previewManipulationCanvas) return;
        let mctx = previewManipulationCanvas.getContext("2d");
        mctx.clearRect(0, 0, updatedCanvasSize * $zoom, updatedCanvasSize * $zoom);
        mctx.drawImage(img, 0, 0, img.width * $zoom, img.height * $zoom);
        mctx.imageSmoothingEnabled = false;

        // Update the overlay canvas too
        previewOverlayCanvas = document.getElementById("previewOverlayCanvas");
        octx = previewOverlayCanvas.getContext("2d");

        // Redraw source canvas so that components (e.g. Histogram) that read data from this get updated
        sctx.drawImage(img, 0, 0, img.width * $zoom, img.height * $zoom);
        sourceCanvas = sourceCanvas;

        const canvasContext = sourceCanvas.getContext("2d");
        processImage(
            canvasContext.getImageData(0, 0, canvasWidth * $zoom, canvasWidth * $zoom),
            previewManipulationCanvas,
            $histogramThreshold.min,
            $histogramThreshold.max
        );

        // Update histogram if visible
        if (isHistogramVisible) {
            onShowHistogram();
        }
    }

    async function saveImage() {
        console.log('SAVING MANIPULATION CANVAS...');

        let exif = {};
        let zeroth = {};
        const created = `${isoDateToDate($selectedPreview?.preview_output?.find((s) => s.slice === sliceIndex)?.start_time)} ${isoDateToTime($selectedPreview?.preview_output?.find((s) => s.slice === sliceIndex)?.start_time)}`;
        let datasetName = `${extractDatasetName($selectedPreview, sliceIndex)}`;
        let sessionName = `${extractSessionName($selectedPreview, sliceIndex)}`;
        let dsd = {
            session: sessionName,
            dataset: datasetName,
            created: created,
            delta: $selectedPreview?.preview_input?.generic_preview_input?.phase_retrieval_settings?.phase_retrieval?.delta,
            mu: $selectedPreview?.preview_input?.generic_preview_input?.phase_retrieval_settings?.phase_retrieval?.mu,
            centershift: $finalFdkSettings?.centershift_object_mm,
            tilt: $selectedPreview?.preview_input?.generic_preview_input?.reconstruction_settings?.tilt_deg,
            slice: sliceIndex
        };
        dsd = JSON.stringify(dsd);
        zeroth[piexif.ImageIFD.ImageDescription] = dsd;
        let exifObj = {"0th":zeroth, "Exif":exif};

        let exifStr = piexif.dump(exifObj);
        let data = previewManipulationCanvas?.toDataURL("image/jpeg");
        var inserted = piexif.insert(exifStr, data);

        let link = document.createElement('a'); //Creates an 'a' element
        let filename = `${sessionName}_${datasetName}_${type}_${sliceIndex}.jpeg`;
        link.download = filename; //Gives the download an output link
        link.href = inserted;
        link.click(); //Clicks the 'a' element we created
        console.log('SAVED CANVAS TO DOWNLOADS DIRECTORY...');
    }

    // BEGIN Histogram
    let isHistogramVisible = false;

    async function onToggleHistogram() {
        if (!isHistogramVisible) {
            await onShowHistogram();
        } else {
            await onHideHistogram();
        }
    }

    async function onShowHistogram() {
        isHistogramVisible = true;
    }

    async function onHideHistogram() {
        isHistogramVisible = false;
        await tick();
    }
    // END Histogram

    function getType(preview) {
        if (!preview) {
            return;
        }
        return $selectedPreview?.preview_input?.preview_input?.preview_type;
    }

    function isCentershiftOptimised(preview) {
        if (!preview) {
            return;
        }
        if (preview?.preview_input?.generic_preview_input?.reconstruction_settings?.centershift_is_optimized) {
            return true;
        }
        return false;
    }

    function isTiltOptimised(preview) {
        if (!preview) {
            return;
        }
        if (preview?.preview_input?.generic_preview_input?.reconstruction_settings?.tilt_is_optimized) {
            return true;
        }
        return false;
    }

    function getCentershiftForPreview(preview) {
        if (!preview) {
            return;
        }

        // Find the correct slice
        const previewSlice = preview?.preview_output?.find((s) => s.slice === sliceIndex);

        // Check if this is a CENTERSHIFT preview and if it is run with optimisation or has a manual centershift set
        if ((previewSlice?.preview_type == "CENTERSHIFT" || previewSlice?.preview_type == "WIDE_CENTERSHIFT") && isCentershiftOptimised(preview)) {
            // We have an optimised centershift value in the middle of the range
            const outputCentershiftMm = previewSlice?.parameter_values?.[31];
            return outputCentershiftMm;
        } else {
            // This is not a centershift preview or a manual centershift was used, so show this value
            const inputCentershiftMm =
                preview.preview_input?.generic_preview_input?.reconstruction_settings?.centershift_object_mm;
            return inputCentershiftMm;
        }
    }

    function getTolerance(preview) {
        if (!preview) {
            return;
        }
        return $selectedPreview?.preview_input?.preview_input?.centershift_tolerance_object_mm?.toPrecision(2);
    }

    function isLocalized(preview) {
        if (!preview) {
            return;
        }
        return $selectedPreview?.preview_input?.generic_preview_input?.reconstruction_settings?.is_localized;
    }

    function getTilt(preview) {
        if (!preview) {
            return;
        }
        return $selectedPreview?.preview_input?.generic_preview_input?.reconstruction_settings?.tilt_deg?.toPrecision(
            3
        );
    }

    let rotating = false;
    let interval;
    function startRotateImageLeft() {
        rotating = true;
        interval = setInterval(() => {
            if (rotating) {
                if ($finalFdkSettings.angle_offset < 360)
                    $finalFdkSettings.angle_offset = $finalFdkSettings.angle_offset + 1;
            }
        }, 50);
    }

    function startRotateImageRight() {
        rotating = true;
        interval = setInterval(() => {
            if (rotating) {
                if ($finalFdkSettings.angle_offset > -360)
                    $finalFdkSettings.angle_offset = $finalFdkSettings.angle_offset - 1;
            }
        }, 50);
    }

    function stopRotateImage() {
        rotating = false;
        clearInterval(interval);
    }
    function activatePan() {
        panActiv = !panActiv;
        if (panActiv) {
            container.style.cursor = "grab";
        } else {
            container.style.cursor = "default";
        }
    }

    function panStart() {
        panImage = true;
    }

    function panMove(event) {
        if (panImage && (event.altKey || panActiv)) {
            container.style.cursor = "grabbing";
            $offset.x += event.movementX;
            $offset.y += event.movementY;
            previewManipulationCanvas.style.translate = `${$offset.x}px ${$offset.y}px`;
        }
    }

    function panEnd() {
        if (panActiv) {
            container.style.cursor = "grab";
        } else {
            container.style.cursor = "default";
        }
        panImage = false;
    }

    function panKey(event) {
        if (event.keyCode === 72) activatePan();
    }

    function zoomOut() {
        if ($zoom === 1) {
            return;
        }
        const bigBox = canvasWidth * $zoom;
        const smallBox = canvasWidth * ($zoom - 1);
        const scale = smallBox / bigBox;

        $offset.x = $offset.x * scale;
        $offset.y = $offset.y * scale;
        previewManipulationCanvas.style.translate = `${$offset.x}px ${$offset.y}px`;
        $zoom = $zoom - 1;
    }

    function onZoomClick(event) {
        if (previewManipulationCanvas.width > 4096 || previewManipulationCanvas.height > 4096) {
            return;
        }

        const smallBox = canvasWidth * $zoom;
        const bigBox = canvasWidth * ($zoom + 1);
        const scale = bigBox / smallBox;
        const newcenterValue = (smallBox - bigBox) / 2;

        if (event) {
            const rect = event.target.getBoundingClientRect();
            const pointX = event.clientX - rect.left - canvasWidth / 2;
            const pointY = event.clientY - rect.top - canvasWidth / 2;
            let testX = ($offset.x - pointX) * scale;
            let testY = ($offset.y - pointY) * scale;

            //const centerValue = (((img.width * ($zoom + 1)) - (canvasWidth * $zoom)) / 2 );
            if ($zoom === 1) {
                testX = testX + newcenterValue;
                testY = testY + newcenterValue;
            }
            $offset.x = testX;
            $offset.y = testY;
        } else {
            $offset.x = $offset.x + newcenterValue;
            $offset.y = $offset.y + newcenterValue;
        }

        previewManipulationCanvas.style.translate = `${$offset.x}px ${$offset.y}px`;
        $zoom = $zoom + 1;
    }

    function resetImage() {
        $offset.x = 0;
        $offset.y = 0;
        $zoom = 1;
        previewManipulationCanvas.style.translate = `${$offset.x}px ${$offset.y}px`;
    }

    function preventOverlayContextMenu() {
        previewOverlayCanvas?.addEventListener("contextmenu", (e) => {
            e.preventDefault();
        });
    }

    function formatDelta(delta) {
        if (!isNumeric(delta) || delta == 0) return "";
        return delta?.toExponential();
    }

    function formatMu(mu) {
        if (!isNumeric(mu) || mu == 0) return "";
        return mu?.toPrecision(3);
    }

    function formatCentershift(cs) {
        if (!isNumeric(cs)) return "";
        return (cs * 1000)?.toFixed(0) + " μm";
    }

    function formatTilt(tilt) {
        if (!isNumeric(tilt)) return "";
        return tilt + " °";
    }

    function formatSlice(slice) {
        if (!isNumeric(slice)) return "";
        return slice;
    }

    function formatIteration(current, total) {
        if (!isNumeric(current)) return "";
        return `${current} / ${total}`;
    }

    function formatIsLocalized(isLocalized) {
        if (isLocalized === undefined) return "";
        return `${isLocalized}`;
    }

    function formatAngle(angle) {
        if (!isNumeric(angle)) return "";
        return angle + " °";
    }

    function lowestOrHighestPhaseRetrieval(delta, mu) {
        if (delta / mu <= getLowestPhaseRetrieval()) return `(lowest)`;
        if (delta / mu >= getHighestPhaseRetrieval()) return `(highest)`;
        return "";
    }
</script>

<div class="preview">
    <h2>{headerText}</h2>
    <p>
        <!--Type: {$selectedPreview?.preview_input?.preview_input?.preview_type !== "WIDE_CENTERSHIFT" ? type : "WIDE_CENTERSHIFT"}-->
        <!--DEBUG: ({getType($selectedPreview)}) &emsp; -->
        Dataset: {extractDatasetName($selectedPreview, sliceIndex) || ""} &emsp;
        Created: {isoDateToDate($selectedPreview?.preview_output?.find((s) => s.slice === sliceIndex)?.start_time)}
        {isoDateToTime($selectedPreview?.preview_output?.find((s) => s.slice === sliceIndex)?.start_time)} &emsp;
        Camera: {getCameraDisplayNameFromCalibrationId($selectedPreview?.preview_input?.generic_preview_input?.calibration_id) || ''}
        <br />
        Phase retrieval &delta;: {formatDelta($selectedPreview?.preview_input?.generic_preview_input?.phase_retrieval_settings?.phase_retrieval?.delta)} &emsp; / &emsp;
        &mu;: {formatMu($selectedPreview?.preview_input?.generic_preview_input?.phase_retrieval_settings?.phase_retrieval?.mu)} &emsp;
        {#if type == 'PHASE_RETRIEVAL' && isNumeric($selectedPreview?.preview_input?.generic_preview_input?.phase_retrieval_settings?.phase_retrieval?.delta) }
            &emsp;=&emsp;{Math.round($selectedPreview?.preview_input?.generic_preview_input?.phase_retrieval_settings?.phase_retrieval?.delta /
                $selectedPreview?.preview_input?.generic_preview_input?.phase_retrieval_settings?.phase_retrieval?.mu * 1e11) / 1e11}
            {lowestOrHighestPhaseRetrieval( $selectedPreview?.preview_input?.generic_preview_input?.phase_retrieval_settings?.phase_retrieval?.delta,
                                            $selectedPreview?.preview_input?.generic_preview_input?.phase_retrieval_settings?.phase_retrieval?.mu)}
        {/if}
        <br />
        {#if $showDebug}
            Centershift (middle): {formatCentershift(getCentershiftForPreview($selectedPreview))} &emsp; <!--Optimised: {isCentershiftOptimised(
                $selectedPreview
            )} &emsp; Tolerance: {getTolerance($selectedPreview) * 1000} μm &emsp;-->
            <br />
        {/if}
        {#if $showDebug}
            Tilt: {formatTilt(getTilt($selectedPreview))} &emsp; <!--Optimised: {isTiltOptimised($selectedPreview)}-->
            <br />
        {/if}
        Slice: {formatSlice(sliceIndex)}&emsp; 
        {#if type != 'FINAL'}
            &emsp; Iteration: {formatIteration(carousel_count, carousel_total)}
        {/if}
        {#if type == 'CENTERSHIFT'}
            <annotation> (for this phase retrieval) </annotation>
        {:else if type == 'BEAMHARD_CUPPING'}
            <annotation> (for this phase retrieval) </annotation>
        {:else if type == 'PHASE_RETRIEVAL'}
            <annotation> (for this centershift and tilt) </annotation>
        {/if}
        &emsp; Localized: {formatIsLocalized(isLocalized($selectedPreview))}&emsp;
        Angle: {formatAngle($finalFdkSettings.angle_offset)}
    </p>
    {#if previewImgSrc == ""}
        <preview-image-placeholder style="--canvasWidth:{canvasWidth}px" />
    {:else}
        <preview-image-toolbar>
            <toolbar-button use:tooltip={{content: "Show histogram", placement: "right"}}>
                <button class:active={isHistogramVisible} on:click={onToggleHistogram}>
                    <ChartHistogram color="currentColor" size="1.5em" />
                </button>
            </toolbar-button>
            <toolbar-button use:tooltip={{content: "Reset view", placement: "right"}}>
                <button on:click={resetImage}>
                    <CropSquare color="currentColor" size="1.5em" />
                </button>
            </toolbar-button>
            <toolbar-button use:tooltip={{content: "Rotate anticlockwise", placement: "right"}}>
                <button on:mousedown={startRotateImageLeft} on:mouseup={stopRotateImage}>
                    <RotateLeft color="currentColor" size="1.5em" />
                </button>
            </toolbar-button>
            <toolbar-button use:tooltip={{content: "Rotate clockwise", placement: "right"}}>
                <button on:mousedown={startRotateImageRight} on:mouseup={stopRotateImage}>
                    <RotateRight color="currentColor" size="1.5em" />
                </button>
            </toolbar-button>
            <toolbar-button use:tooltip={{content: "Zoom in", placement: "right"}}>
                <button on:click={() => onZoomClick()}>
                    <MagnifyPlus color="currentColor" size="1.5em" />
                </button>
            </toolbar-button>
            <toolbar-button use:tooltip={{content: "Zoom out", placement: "right"}}>
                <button on:click={() => zoomOut()}>
                    <MagnifyMinus color="currentColor" size="1.5em" />
                </button>
            </toolbar-button>
            <toolbar-button use:tooltip={{content: "Pan", placement: "right"}}>
                <button class:active={panActiv} on:click={activatePan}>
                    <HandBackRight color="currentColor" size="1.5em" />
                </button>
            </toolbar-button>
            <toolbar-button  use:tooltip={{content: "Save this preview slice as JPEG", placement: "right"}}>
                <button on:click={saveImage}>
                    <ContentSave color="currentColor" size="1.5em" />
                </button>
            </toolbar-button>
            {#if type === "FINAL"}
                <toolbar-button use:tooltip={{content: "Mark region-of-interest (ROI) to use when reconstructing volume", placement: "right"}}>
                    <button class:active={drawStatus} on:click={() => (drawStatus = !drawStatus)}>
                        <VectorSquare color="currentColor" size="1.5em" />
                    </button>
                </toolbar-button>
            {/if}
        </preview-image-toolbar>
        <preview-image-carousel>
            <preview-image-carousel-sidebar>
                <!-- svelte-ignore a11y-click-events-have-key-events -->
                <previous-button
                    style="visibility:{getNumberOfAvailablePreviewsForCurrentSlice(sliceIndex) > 1
                        ? 'visible'
                        : 'hidden'};"
                    on:click={showPreviousPreview(sliceIndex)}
                    use:tooltip={{content: "Show previous preview for this slice"}}>
                    <ChevronLeft color="currentColor" size="1.5em" />
                </previous-button>
            </preview-image-carousel-sidebar>
            <preview-image-box
                bind:this={container}
                on:dblclick={(event) => onZoomClick(event)}
                on:mousemove={panMove}
                on:mousedown={(e) => panStart(e)}
                on:mouseup={(e) => panEnd(e)}
                on:mouseleave={() => (panImage = false)}
                style="--canvasWidth:{canvasWidth}px">
                <!-- svelte-ignore a11y-mouse-events-have-key-events a11y-missing-attribute -->
                <img
                    crossorigin="anonymous"
                    on:load={onLoadImage}
                    src={previewImgSrc}
                    class={isNumeric($finalFdkSettings?.angle_offset) ? "rotate" : ""}
                    style="--angle:{-$finalFdkSettings.angle_offset}deg; visibility:hidden" />
                <!-- svelte-ignore a11y-mouse-events-have-key-events -->
                <canvas
                    id="previewManipulationCanvas"
                    width={canvasWidth * $zoom}
                    height={canvasWidth * $zoom}
                    class={isNumeric($finalFdkSettings?.angle_offset) ? "rotate" : ""}
                    style="--angle:{-$finalFdkSettings.angle_offset}deg;" />
                <!-- svelte-ignore a11y-mouse-events-have-key-events -->
                <canvas id="previewOverlayCanvas" width={canvasWidth} height={canvasWidth}/>
            </preview-image-box>
            <preview-image-carousel-sidebar>
                <!-- svelte-ignore a11y-click-events-have-key-events -->
                <next-button
                    style="visibility:{getNumberOfAvailablePreviewsForCurrentSlice(sliceIndex) > 1
                        ? 'visible'
                        : 'hidden'};"
                    on:click={showNextPreview(sliceIndex)}
                    use:tooltip={{content: "Show next preview for this slice"}}>
                    <ChevronRight color="currentColor" size="1.5em" />
                </next-button>
            </preview-image-carousel-sidebar>
        </preview-image-carousel>
    {/if}
    <slot>
        <p>No content is set for this preview</p>
    </slot>
    {#if isHistogramVisible}
        <Histogram
            {canvasWidth}
            zoom={$zoom}
            {sourceCanvas}
            manipulationCanvas={previewManipulationCanvas}
            bind:minHistogramThreshold={$histogramThreshold.min}
            bind:maxHistogramThreshold={$histogramThreshold.max} />
    {/if}
</div>

<style>
    preview-image-box {
        display: inline-flex;
        height: var(--canvasWidth);
        width: var(--canvasWidth);
        max-height: 2048px;
        max-width: 2048px;
        margin-bottom: 20px;
        overflow: hidden;
        background-color: black;
        position: relative;
    }

    preview-image-placeholder {
        display: inline-block;
        width: var(--canvasWidth);
    }

    preview-image-carousel {
        display: flex;
        align-items: center;
        margin-left: 24px;
        margin-right: 24px;
    }

    previous-button,
    next-button {
        display: inline-flex;
        background-color: transparent;
        color: var(--var-theme-background-color);
        margin: auto;
    }

    previous-button:hover,
    next-button:hover {
        cursor: pointer;
        color: var(--var-theme-hover-background-color);
    }

    preview-image-carousel-sidebar {
        display: flex;
        flex-direction: column;
        align-self: stretch;
        justify-content: center;
        height: 100%;
        margin: auto;
    }
    preview-image-toolbar {
        display: flex;
        flex-direction: column;
        float: right;
        margin-left: -20px;
    }

    .rotate {
        transform: rotate(var(--angle));
    }
    #previewManipulationCanvas {
        position: absolute;
        user-select: none;
        /*filter: url(#revspectrum);*/
        pointer-events: auto;
    }

    #previewOverlayCanvas {
        transform: translate(-100%, 0);
        pointer-events: auto; /* Needed for drawing but unfortunately blocks contextmenu on underlying canvas */
    }

    toolbar-button {
        display: inline-flex;
    }

    toolbar-button button {
        padding: 8px;
        display: flex;
    }

    toolbar-button .active {
        background-color: var(--var-theme-hover-background-color);
        box-shadow: none;
    }
</style>
